<template>
    <transition name="toast" appear>
        <div v-if="visible" :class="['toast', `toast--${type}`]" @click="close">
            <div class="toast__content">
                <div class="toast__title" v-if="title">{{ title }}</div>
                <div class="toast__message">{{ message }}</div>
            </div>

            <svg-icon name="close" />
        </div>
    </transition>
</template>

<script>
export default {
    props: {
        type: {
            type: String,
            default: "info",
            validator: value =>
                ["success", "error", "warning", "info"].includes(value)
        },
        title: {
            type: String,
            default: ""
        },
        message: {
            type: String,
            required: true
        },
        duration: {
            type: Number,
            default: 5000
        },
        autoClose: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            visible: false,
            timer: null
        };
    },
    mounted() {
        this.show();
    },
    beforeDestroy() {
        this.clearTimer();
    },
    methods: {
        show() {
            this.visible = true;

            if (this.autoClose && this.duration > 0) {
                this.timer = setTimeout(() => {
                    this.close();
                }, this.duration);
            }
        },
        close() {
            this.visible = false;
            this.clearTimer();

            this.$emit("close");

            setTimeout(() => {
                this.$destroy();
                this.$el.remove();
            }, 300);
        },
        clearTimer() {
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
        }
    }
};
</script>

<style lang="scss">
.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    pointer-events: none;
}

.toast-container .toast {
    pointer-events: auto;
}

@media (max-width: 768px) {
    .toast-container {
        top: 10px;
        right: 10px;
        left: 10px;
    }
}

.toast .icon-wrap {
    line-height: 1;

    .icon {
        transform: none;
        width: 12px;
        height: 12px;

        line {
            stroke: #333;
        }
    }
}
</style>

<style lang="scss" scoped>
.toast {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    margin-bottom: 12px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-left: 4px solid;
    min-width: 320px;
    max-width: 400px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    }

    &--success {
        border-left-color: #10b981;
        background: #d5fff1;
    }

    &--error {
        border-left-color: #ef4444;
        background: #ffe5e5;
    }

    &--warning {
        border-left-color: #f59e0b;
        background: #fff8e5;
    }

    &--info {
        border-left-color: #3b82f6;
        background: #e5f0ff;
    }
}

.toast__content {
    flex: 1;
    min-width: 0;
}

.toast__title {
    font-weight: 600;
    font-size: 14px;
    color: #1f2937;
    margin-bottom: 4px;
}

.toast__message {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.4;
}

.toast-enter-active,
.toast-leave-active {
    transition: all 0.3s ease;
}

.toast-enter {
    opacity: 0;
    transform: translateX(100%);
}

.toast-leave-to {
    opacity: 0;
    transform: translateX(100%);
}

.toast-enter-to,
.toast-leave {
    opacity: 1;
    transform: translateX(0);
}
</style>
